import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COPY_COMMAND,
  PASTE_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $createTextNode,
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isParentElementRTL,
  $wrapNodes,
  $isAtNodeEnd
} from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode
} from "@lexical/list";
import { createPortal } from "react-dom";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode
} from "@lexical/rich-text";
import {
  $createCodeNode,
  $isCodeNode,
  getDefaultCodeLanguage,
  getCodeLanguages
} from "@lexical/code";

import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from '@lexical/markdown';

import { Menu } from '@headlessui/react'
import TextArea from '../../../documentation/field_types/RichText'
import useApi from '../../../api';
import { v4 as uuidv4 } from 'uuid';

import { StreamParagraph, $createStreamParagraphNode } from '../custom_nodes/CustomNodes'

const LowPriority = 1;

const supportedBlockTypes = new Set([
  "paragraph",
  "quote",
  "code",
  "h1",
  "h2",
  "ul",
  "ol"
]);

const blockTypeToBlockName = {
  code: "Code Block",
  h1: "Large Heading",
  h2: "Small Heading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  ol: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
  ul: "Bulleted List"
};

function Divider() {
  return <div className="w-[1px] bg-[#eee] m-[0_4px]" />;
}

function positionEditorElement(editor, rect) {
  if (rect === null) {
    editor.style.opacity = "0";
    editor.style.top = "-1000px";
    editor.style.left = "-1000px";
  } else {
    editor.style.opacity = "1";
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

function FloatingLinkEditor({ editor }) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEditMode, setEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl("");
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== "link-input") {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority
      )
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  return (
    <div ref={editorRef} className="absolute z-40 -top-full left-full mt-[-6px] max-w-xs w-full opacity-0 bg-white shadow-lg rounded-lg transition-opacity duration-500">
      {isEditMode ? (
        <input
          ref={inputRef}
          className="block w-[calc(100%-24px)] mx-3 my-2 p-2 rounded-full bg-gray-200 text-base text-gray-800 border-0 outline-none relative font-inherit"
          value={linkUrl}
          onChange={(event) => {
            setLinkUrl(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (lastSelection !== null) {
                if (linkUrl !== "") {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                }
                setEditMode(false);
              }
            } else if (event.key === "Escape") {
              event.preventDefault();
              setEditMode(false);
            }
          }}
        />
      ) : (
        <>
          <div className="block text-blue-600 no-underline whitespace-nowrap overflow-hidden mr-7 text-ellipsis hover:underline">
            <a href={linkUrl} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </a>
            <div
              className="inline-block p-[6px] rounded-lg cursor-pointer m-0.5 w-5 h-5"
              role="button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditMode(true);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Select({ onChange, className, options, value }) {
  return (
    <select className={className} onChange={onChange} value={value}>
      <option hidden={true} value="" />
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function BlockOptionsDropdownList({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptionsDropDown
}) {
  const dropDownRef = useRef(null);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, toolbarRef]);

  useEffect(() => {
    const dropDown = dropDownRef.current;
    const toolbar = toolbarRef.current;

    if (dropDown !== null && toolbar !== null) {
      const handle = (event) => {
        const target = event.target;

        if (!dropDown.contains(target) && !toolbar.contains(target)) {
          setShowBlockOptionsDropDown(false);
        }
      };
      document.addEventListener("click", handle);

      return () => {
        document.removeEventListener("click", handle);
      };
    }
  }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

  const formatParagraph = () => {
    if (blockType !== "paragraph") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatLargeHeading = () => {
    if (blockType !== "h1") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h1"));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatSmallHeading = () => {
    if (blockType !== "h2") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h2"));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatBulletList = () => {
    if (blockType !== "ul") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatNumberedList = () => {
    if (blockType !== "ol") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatCode = () => {
    if (blockType !== "code") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  return (
    <div className="absolute z-10 block shadow-xl rounded-lg min-w-[100px] min-h-[40px] bg-white" ref={dropDownRef}>
      <button className="m-[0_8px] p-[8px] mt-2 text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatParagraph}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain text-gray-400">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
        <span className="text">Normal</span>
        {blockType === "paragraph" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
      <button className="m-[0_8px] p-[8px] text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatLargeHeading}>
        <span className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain text-gray-400">h1</span>
        <span className="text">Large Heading</span>
        {blockType === "h1" && <span className="flex w-[20px] h-[20px] bg-contain" />}   
      </button>
      <button className="m-[0_8px] p-[8px] text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatSmallHeading}>
      <span className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain text-gray-400">h2</span>
        <span className="text">Small Heading</span>
        {blockType === "h2" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
      <button className="m-[0_8px] p-[8px] text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatBulletList}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain stroke-gray-400">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
        <span className="text">Bullet List</span>
        {blockType === "ul" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
      <button className="m-[0_8px] p-[8px] text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatNumberedList}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain stroke-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
      </svg>
        <span className="text">Numbered List</span>
        {blockType === "ol" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
      <button className="m-[0_8px] p-[8px] text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatQuote}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain stroke-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
      </svg>
        <span className="text">Quote</span>
        {blockType === "quote" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
      <button className="m-[0_8px] p-[8px] mb-2 text-[#050505] cursor-pointer leading-[16px] text-[15px] flex items-center flex-row flex-shrink-0 bg-white rounded-[8px] border-0 min-w-[268px] hover:bg-gray-100" onClick={formatCode}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="flex w-[20px] h-[20px] select-none mr-[12px] leading-[16px] bg-contain stroke-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
      </svg>
        <span className="text">Code Block</span>
        {blockType === "code" && <span className="flex w-[20px] h-[20px] bg-contain" />}
      </button>
    </div>
  );
}

export default function ToolbarPlugin({ content, docId, field, updateSchema, path, mode, userInput, contextDocs, setUserInput, messageSent, setMessageSent, setModalVisible, modalVisible}) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState(null);
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] = useState(
    false
  );
  const [codeLanguage, setCodeLanguage] = useState("");
  const [isRTL, setIsRTL] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [updatedContent, setUpdatedContent] = useState(content);

  const { get, getStream, post, postStream, put, patch, remove } = useApi();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
          if ($isCodeNode(element)) {
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
          }
        }
      }
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }

    const stringifiedEditorState = JSON.stringify(
      editor.getEditorState().toJSON(),
    );

    const parsedEditorState = editor.parseEditorState(stringifiedEditorState);
    
    const editorStateTextString = parsedEditorState.read(() => $getRoot().getTextContent())

    console.log(editorStateTextString);

    console.log(editor.getEditorState().toJSON());

    if (mode === "content") {
      if (updateSchema) {
        updateSchema({ value: editor.getEditorState().toJSON(), text_value: editorStateTextString });
      }
    } else {
      updateSchema({ value: editor.getEditorState().toJSON(), text_value: editorStateTextString }, path, field.name, 'updateFormFieldValue');
    }
  }, [editor]);

  useEffect(() => {
    // check if content is JSON
    if (mode === 'content' && typeof content === 'string') {
      editor.update(() => {
        $convertFromMarkdownString(content, TRANSFORMERS);
      });
    }

  }, [content]);


  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const codeLanguges = useMemo(() => getCodeLanguages(), []);
  const onCodeLanguageSelect = useCallback(
    (e) => {
      editor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(e.target.value);
          }
        }
      });
    },
    [editor, selectedElementKey]
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  async function plainTextToLexicalState(text) {

    return new Promise((resolve) => {
        editor.registerUpdateListener(({editorState}) => {
            resolve(JSON.stringify(editorState));
        });


        editor.update(() => {
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode(text);
            paragraph.append(textNode);
            $getRoot().append(paragraph);
        });
    });
}

const appendContentToDocument = (content, paragraphId) => {
  let id = paragraphId ? paragraphId : null;
  editor.update(() => {
    let paragraph;
    if (paragraphId) {
      paragraph = $getNodeByKey(paragraphId); // Fetch the latest instance by ID
      if (!paragraph) {
        // Fallback in case the paragraph does not exist
        paragraph = $createParagraphNode();
        console.log(paragraph)
        id = paragraph.getKey();
        console.log(id)
        $getRoot().append(paragraph);
      }
    } else {
      paragraph = $createParagraphNode();
      id = paragraph.getKey();
      console.log(paragraph)
      console.log(id)
      $getRoot().append(paragraph);
    }

    const textNode = $createTextNode(content);
    paragraph.append(textNode);
  });

  return id ; // Return the ID to keep track
};


const consumeStream = async (stream) => {
  const reader = stream.getReader();
  let receivedText = ''; // To accumulate the converted text
  let conversationId = '';
  // generate guid for stream

  // editor.registerUpdateListener(({editorState}) => {
  //   resolve(JSON.stringify(editorState));
  // });

  let paragraphId = null;

    try {
        let chunkText = ''; // To store the chunk text
  
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
  
            // Convert the Uint8Array to String
            const chunk = new TextDecoder().decode(value);
            chunkText += chunk;
  
            // Split the chunk text by newline character
            const chunks = chunkText.split('\n');
  
            // Process each chunk
            for (let i = 0; i < chunks.length - 1; i++) {
                const chunk = chunks[i];
  
                // Attempt to parse JSON text and handle the data
                try {
                    const data = JSON.parse(chunk);
  
                    if (data.type === 'assistant' && data.content) {
                        console.log(data.content)
                        // updateSchema(field.value + receivedText, path, field.name, 'updateFormFieldValue')
                        receivedText += data.content;
                        // setAssistantResponse(data.content)
                        paragraphId = appendContentToDocument(data.content, paragraphId);    
                    }
  
                } catch (error) {
                    console.error("Error parsing JSON from chunk", error);
                }
            }
  
            // Store the remaining chunk for the next iteration
            chunkText = chunks[chunks.length - 1];
        }
    } catch (error) {
        console.error("Error reading from stream", error);
    } finally {
        console.log(receivedText)
    }


};

  const sendMessage = async (userInput) => {
    console.log('sending message')
    const data = {
        doc_id: docId,
        field,
        message: { role: "user", content: userInput, created_at: new Date().toISOString() },
        context_docs: contextDocs
    }
    console.log(data)

      postStream('/field-assistant', data)
      .then(async ({ stream }) => {
          const convId = await consumeStream(stream);

      })
      .catch(error => console.error('Streaming error:', error));

    // setLoading(false);
  };

  useEffect(() => {
    if (field && !field.value.value) {
      plainTextToLexicalState(content);
    }
  }, []);

  useEffect(() => {
    if (messageSent) {
      sendMessage(userInput);
      setUserInput('');
      setMessageSent(false);
    }
  }, [messageSent]);





  return (
    <div className="flex mb-[1px] bg-gray-50 p-[4px] rounded-tl-lg rounded-tr-lg align-middle" ref={toolbarRef}>
      <button
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND);
        }}
        className="border-0 flex bg-none rounded-lg p-[8px] cursor-pointer align-middle disabled:opacity-50 disabled:cursor-not-allowed mr-[2px]"
        aria-label="Undo"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
        </svg>

      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND);
        }}
        className="border-0 flex bg-none rounded-lg p-[8px] cursor-pointer align-middle disabled:opacity-50 disabled:cursor-not-allowed mr-[2px]"
        aria-label="Redo"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="m15 15 6-6m0 0-6-6m6 6H9a6 6 0 0 0 0 12h3" />
      </svg>

      </button>
      <Divider />
      {supportedBlockTypes.has(blockType) && (
        <>
          <button
            className="items-center border-0 flex bg-none rounded-lg p-[8px] cursor-pointer align-middle hover:bg-gray-100"
            onClick={() =>
              setShowBlockOptionsDropDown(!showBlockOptionsDropDown)
            }
            aria-label="Formatting Options"
          >
            <span className={"icon block-type " + blockType} />
            <span className="text">{blockTypeToBlockName[blockType]}</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-5 h-5 stroke-gray-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>

          </button>
          {showBlockOptionsDropDown &&
            createPortal(
              <BlockOptionsDropdownList
                editor={editor}
                blockType={blockType}
                toolbarRef={toolbarRef}
                setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
              />,
              document.body
            )}
          <Divider />
        </>
      )}
      {blockType === "code" ? (
        <>
          <Select
            className="toolbar-item code-language"
            onChange={onCodeLanguageSelect}
            options={codeLanguges}
            value={codeLanguage}
          />
          <i className="chevron-down inside" />
        </>
      ) : (
        <>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
              console.log("bold", isBold);
            }}
            className={"text-gray-700 font-bold px-4 text-lg rounded-lg hover:bg-gray-200" + (isBold ? " bg-gray-200" : "")}
            aria-label="Format Bold"
          >
            B
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
            }}
            className={"text-gray-700 font-semibold italic px-4 text-lg rounded-lg hover:bg-gray-200" + (isItalic ? " bg-gray-200" : "")}
            aria-label="Format Italics"
          >
            i
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
            }}
            className={"text-gray-700 font-semibold px-4 text-lg rounded-lg hover:bg-gray-200" + (isUnderline ? " bg-gray-200" : "")}
            aria-label="Format Underline"
          >
           <u>
            U
           </u>
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
            }}
            className={
              "text-gray-700 font-semibold px-4 text-lg rounded-lg hover:bg-gray-200" + (isStrikethrough ? " bg-gray-200" : "")
            }
            aria-label="Format Strikethrough"
          >
            <div className="line-through">S</div>
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
            }}
            className={"rounded-lg hover:bg-gray-200 px-3 items-center" + (isCode ? " bg-gray-200" : "")}
            aria-label="Insert Code"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
          </svg>
          </button>
          <button
            onClick={insertLink}
            className={"rounded-lg hover:bg-gray-200 px-3 items-center" + (isLink ? " bg-gray-200" : "")}
            aria-label="Insert Link"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
          </svg>

          </button>
          {isLink &&
            createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
          <Divider />
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
            }}
            className="rounded-lg hover:bg-gray-200 px-3 items-center"
            aria-label="Left Align"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
          </svg>

          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
            }}
            className="rounded-lg hover:bg-gray-200 px-3 items-center"
            aria-label="Center Align"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>

          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
            }}
            className="rounded-lg hover:bg-gray-200 px-3 items-center"
            aria-label="Right Align"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25" />
          </svg>

          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
            }}
            className="rounded-lg hover:bg-gray-200 px-3 items-center"
            aria-label="Justify Align"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 stroke-gray-700">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
          </svg>

          </button>
          <div className="flex flex-grow items-center justify-end">
          {modalVisible || mode === "content" ? (
              <button onClick={() => setModalVisible(false)} type="button" data-tooltip-target="tooltip-fullscreen" className="p-2 text-gray-500 rounded cursor-pointer sm:ms-auto hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
                <span className="sr-only">Close</span>
              </button>
          ) : (

              <button onClick={() => setModalVisible(true)} type="button" data-tooltip-target="tooltip-fullscreen" className="p-2 text-gray-500 rounded cursor-pointer sm:ms-auto hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600">
                  <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 19 19">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 1h5m0 0v5m0-5-5 5M1.979 6V1H7m0 16.042H1.979V12M18 12v5.042h-5M13 12l5 5M2 1l5 5m0 6-5 5" />
                  </svg>
                  <span className="sr-only">Full screen</span>
              </button>
              
          )}
          </div>
        </>
      )}
    </div>
  );
}
